import {Component, computed, effect, inject, signal} from '@angular/core';
import {NavigationEnd, Router, RouterLink} from '@angular/router';
import {toSignal} from '@angular/core/rxjs-interop';
import {filter, map} from 'rxjs';
import {Ripple} from 'primeng/ripple';
import {Tooltip} from 'primeng/tooltip';
import {NgOptimizedImage} from "@angular/common";
import {TranslatePipe} from '@ngx-translate/core';
import {ElciaIcon, IconType} from '../icon/icon';
import {RouteUri} from '../../constants/route-uri';
import {CurrentUserService} from '../../services/current-user.service';

interface MenuNavItem {
  label: string;
  icon: IconType;
  routerLink: string;
  visible?: boolean;
  // Extra URL prefixes that should keep this item highlighted (e.g. forms routed outside the parent path)
  activeMatch?: string[];
}

@Component({
  selector: 'elcia-main-menu',
  imports: [RouterLink, Ripple, Tooltip, NgOptimizedImage, TranslatePipe, ElciaIcon],
  templateUrl: './main-menu.html',
  styleUrl: './main-menu.scss'
})
export class MainMenu {
  currentUserService = inject(CurrentUserService);
  private readonly router = inject(Router);

  isExpanded = signal<boolean>(true);
  width = computed(() => this.isExpanded() ? 240 : 76);

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this.router.url),
    ),
    {initialValue: this.router.url},
  );

  menuItems = computed<MenuNavItem[]>(() => {
    const hasFinancePermission = this.currentUserService.hasFinanceAccess();

    return [
      {label: 'app.dashboard', icon: 'dashboard', routerLink: RouteUri.DASHBOARD},
      {label: 'app.directory', icon: 'users', routerLink: '/directory'},
      {
        label: 'entity.todos',
        icon: 'todo',
        routerLink: RouteUri.TODOS,
        visible: this.currentUserService.hasTodoAccess(),
      },
      {
        label: 'entity.documents',
        icon: 'file',
        routerLink: RouteUri.DOCUMENTS,
        visible: this.currentUserService.hasDocumentAccess(),
      },
      {
        label: 'entity.timesheets',
        icon: 'clock',
        routerLink: RouteUri.TIMESHEETS,
        visible: this.currentUserService.hasTimesheetAccess(),
      },
      {
        label: 'app.finances',
        icon: 'finances',
        routerLink: '/finances',
        activeMatch: ['/' + RouteUri.INVOICE_FORM, '/' + RouteUri.CREDIT_NOTE_FORM],
        visible: hasFinancePermission,
      },
      {
        label: 'entity.products',
        icon: 'tag',
        routerLink: RouteUri.PRODUCTS,
        visible: this.currentUserService.hasManageProduct(),
      },
      {
        label: 'app.settings',
        icon: 'setting',
        routerLink: RouteUri.SETTINGS,
        visible: this.currentUserService.hasSettingsAccess(),
      },
    ];
  });

  visibleItems = computed<MenuNavItem[]>(() => this.menuItems().filter(item => item.visible !== false));

  private readonly STORAGE_KEY = 'menu-expanded';

  // Save expanded state to localStorage whenever it changes
  private saveExpandedState = effect(() => {
    localStorage.setItem(this.STORAGE_KEY, String(this.isExpanded()));
  });

  constructor() {
    // Restore expanded state from localStorage on init
    this.isExpanded.set(localStorage.getItem(this.STORAGE_KEY) !== 'false');
  }

  protected toggleSize() {
    this.isExpanded.update(b => !b);
  }

  // Highlight an item when the URL matches its routerLink or any extra activeMatch prefix (segment-aware)
  protected isActive(item: MenuNavItem): boolean {
    const url = this.currentUrl().split(/[?#]/)[0];
    return [item.routerLink, ...(item.activeMatch ?? [])]
      .map(prefix => prefix.startsWith('/') ? prefix : `/${prefix}`)
      .some(prefix => url === prefix || url.startsWith(`${prefix}/`));
  }
}
